<template>
  <v-container fluid class="dashboard-container pa-0 pb-12">
    <!-- Header: Identity & Daily Pulse -->
    <v-sheet color="white" class="pa-6 pa-md-8 mb-6 border-bottom shadow-xs">
      <v-row align="center" justify="space-between">
        <v-col cols="12" lg="6">
          <div class="d-flex align-center">
            <v-avatar size="52" color="primary lighten-5" class="mr-4 rounded-xl">
              <v-icon color="primary">fas fa-chart-line</v-icon>
            </v-avatar>
            <div>
              <div class="d-flex align-center flex-wrap" style="gap: 8px">
                <h1 class="text-h5 font-weight-black primary--text tracking-tight mb-0">
                  Dashboard de Ventas
                </h1>
                <v-chip v-if="asOf" x-small label color="blue-grey lighten-5" class="blue-grey--text text--darken-3 font-weight-medium">
                  <v-icon left x-small>fas fa-clock</v-icon>
                  Actualizado {{ formattedAsOf }}
                </v-chip>
              </div>
              <p class="body-2 grey--text text--darken-1 font-weight-medium mb-0 mt-1">
                Supervisión comercial por ejecutivo y cliente · Período:
                <span class="font-weight-bold grey--text text--darken-3">{{ periodLabel }}</span>
                · <span class="font-weight-bold primary--text">{{ selectedLineName }}</span>
              </p>
            </div>
          </div>
        </v-col>

        <!-- Daily Pulse Ticker & Action -->
        <v-col cols="12" lg="6" class="d-flex align-center justify-lg-end flex-wrap" style="gap: 12px">
          <!-- Daily Pulse Card -->
          <div class="daily-pulse-card d-flex align-center px-4 py-2 rounded-xl">
            <div class="pulse-indicator mr-3"></div>
            <div>
              <div class="caption font-weight-bold text-uppercase grey--text text--darken-1">Pulso Hoy</div>
              <div class="d-flex align-baseline" style="gap: 6px">
                <span class="font-weight-black text-body-1 grey--text text--darken-4">
                  {{ kpis.today_confirmed | moneda }}
                </span>
                <span class="caption font-weight-bold" :class="deltaClass(kpis.vs_yesterday_pct)">
                  {{ deltaLabel(kpis.vs_yesterday_pct) }} vs ayer
                </span>
              </div>
            </div>
          </div>

          <v-btn
            outlined
            color="primary"
            class="rounded-xl px-4 font-weight-bold text-none shadow-xs"
            :loading="downloadingPdf"
            @click="downloadExecutivePdf"
          >
            <v-icon left small color="error">fas fa-file-pdf</v-icon>
            Exportar PDF
          </v-btn>

          <v-btn
            color="primary"
            depressed
            class="rounded-xl px-5 font-weight-bold text-none shadow-xs"
            :loading="loading"
            @click="load(true)"
          >
            <v-icon left small>fas fa-sync-alt</v-icon>
            Actualizar
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>

    <div class="px-6 px-md-8">
      <!-- Time Control & Quick Presets Bar -->
      <v-card flat outlined class="filter-card pa-4 mb-6 shadow-xs">
        <v-row align="center" dense>
          <v-col cols="12" md="7">
            <div class="d-flex align-center flex-wrap" style="gap: 6px">
              <span class="caption font-weight-bold grey--text text--darken-2 mr-2 text-uppercase">
                Rango rápido:
              </span>
              <v-btn
                v-for="preset in presets"
                :key="preset.id"
                x-small
                depressed
                :color="activePreset === preset.id ? 'primary' : 'grey lighten-4'"
                :class="activePreset === preset.id ? 'white--text font-weight-bold' : 'grey--text text--darken-3 font-weight-medium'"
                class="rounded-lg text-none px-3 py-2"
                @click="applyPreset(preset)"
              >
                {{ preset.label }}
              </v-btn>
            </div>
          </v-col>

          <v-col cols="12" md="5" class="d-flex align-center justify-md-end flex-wrap" style="gap: 8px">
            <div class="d-flex align-center" style="gap: 8px">
              <v-text-field
                v-model="startDate"
                label="Desde"
                outlined
                dense
                hide-details
                type="date"
                class="compact-date-input"
                @change="onManualDateChange"
              />
              <span class="grey--text">—</span>
              <v-text-field
                v-model="endDate"
                label="Hasta"
                outlined
                dense
                hide-details
                type="date"
                class="compact-date-input"
                @change="onManualDateChange"
              />
            </div>
          </v-col>
        </v-row>
        <v-row dense class="mt-2">
          <v-col cols="12" md="4">
            <v-select
              v-model="line"
              :items="businessLineOptions"
              item-value="id"
              item-text="name"
              label="Línea de negocio"
              prepend-inner-icon="fas fa-layer-group"
              outlined
              dense
              hide-details
            />
          </v-col>
        </v-row>
      </v-card>

      <!-- Error State with Retry Button -->
      <v-alert
        v-if="error"
        dense
        outlined
        type="error"
        class="mb-6 rounded-xl bg-white"
      >
        <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px">
          <span>{{ error }}</span>
          <v-btn small color="error" depressed class="rounded-lg text-none font-weight-bold" @click="load(true)">
            <v-icon left small>fas fa-redo</v-icon>
            Reintentar
          </v-btn>
        </div>
      </v-alert>

      <!-- Loading Skeletons -->
      <v-row v-if="loading && !asOf" dense class="mb-6">
        <v-col v-for="i in 4" :key="'sk-' + i" cols="12" sm="6" md="3">
          <v-skeleton-loader type="card" height="136" class="rounded-xl shadow-xs" />
        </v-col>
      </v-row>

      <template v-else>
        <!-- Row 1: Hero KPIs (4 Master Cards) -->
        <v-row dense class="mb-6">
          <!-- KPI 1: Venta Confirmada (North Star) -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100 is-primary">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Venta Confirmada</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Monto neto de cotizaciones que ya cuentan con Orden de Trabajo (OT) generada.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value primary--text text--darken-3">
                  {{ kpis.confirmed | moneda }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">
                      {{ kpis.confirmed_count || 0 }} OT confirmadas
                    </span>
                    <span class="caption font-weight-black" :class="deltaClass(kpis.vs_previous_pct)">
                      {{ deltaLabel(kpis.vs_previous_pct) }} vs prev.
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- KPI 2: Cotizado Global -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Total Cotizado</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Suma acumulada de todas las notas de venta cotizadas creadas en el período.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value grey--text text--darken-4">
                  {{ kpis.quoted | moneda }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="caption font-weight-bold grey--text text--darken-1">
                    {{ kpis.count || 0 }} cotizaciones emitidas
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- KPI 3: Tasa de Conversión -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Efectividad de Cierre</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Porcentaje del monto cotizado que se convirtió en venta confirmada (Vendido ÷ Cotizado).</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value teal--text text--darken-3">
                  {{ kpis.conversion || 0 }}%
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">Vendido / Cotizado</span>
                    <span class="caption font-weight-bold teal--text text--darken-2">
                      Pipeline: {{ kpis.pipeline | moneda }}
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- KPI 4: Margen Teórico Global -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Margen Teórico</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Margen promedio ponderado de las órdenes confirmadas según costos teóricos de cotización.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value" :class="marginClass(kpis.confirmed_margin)">
                  {{ (kpis.confirmed_margin || 0) | percent(1) }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">
                      Utilidad: {{ kpis.confirmed_profit | moneda }}
                    </span>
                    <span class="caption font-weight-bold" :class="deltaClass(kpis.vs_previous_margin_pp)">
                      {{ deltaPpLabel(kpis.vs_previous_margin_pp) }} vs prev.
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Venta por Línea de Negocio -->
        <v-card class="panel-card mb-6 shadow-xs" flat>
          <div class="d-flex align-center justify-space-between mb-1 flex-wrap" style="gap: 8px">
            <div class="panel-title">Por Línea de Negocio</div>
            <span class="caption grey--text font-weight-bold">Volumen vendido en m² y unidades según la línea</span>
          </div>
          <div class="panel-sub mb-4">Todo el período, sin aplicar el filtro de línea · clic en una fila para filtrar el dashboard</div>
          <div v-if="!lineBreakdown.length" class="empty-mini py-6 text-center">Sin cotizaciones en este período</div>
          <v-simple-table v-else dense class="line-table">
            <thead>
              <tr>
                <th>Línea</th>
                <th class="text-end">Cotizado</th>
                <th class="text-end">Vendido</th>
                <th class="text-end">Conversión</th>
                <th class="text-end">Margen teórico</th>
                <th class="text-end">m² vendidos</th>
                <th class="text-end">Unid. vendidas</th>
                <th class="text-end">Cotiz. / OTs</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in lineBreakdown"
                :key="'l-' + row.id"
                class="line-row"
                :class="{ 'is-selected-line': String(line) === String(row.id) }"
                @click="toggleLine(row.id)"
              >
                <td class="font-weight-bold">{{ row.name }}</td>
                <td class="text-end">{{ row.quoted | moneda }}</td>
                <td class="text-end font-weight-black">{{ row.confirmed | moneda }}</td>
                <td class="text-end">{{ row.conversion }}%</td>
                <td class="text-end font-weight-bold" :class="marginClass(row.confirmed_margin)">{{ row.confirmed_margin | percent(1) }}</td>
                <td class="text-end">{{ row.confirmed_m2 | number('0,0.0') }}</td>
                <td class="text-end">{{ row.confirmed_qty | number('0,0') }}</td>
                <td class="text-end">{{ row.count }} / {{ row.won }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>

        <!-- Row 2: Distribución de Salud de Margen (Segmented Stacked Bar & Quick Tiers) -->
        <v-card flat class="panel-card mb-6 shadow-xs">
          <div class="d-flex align-center justify-space-between mb-3 flex-wrap" style="gap: 8px">
            <div>
              <div class="d-flex align-center">
                <h2 class="panel-title mb-0">Distribución de Salud de Margen</h2>
                <v-tooltip bottom max-width="320">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" small color="grey" class="ml-2">fas fa-info-circle</v-icon>
                  </template>
                  <span>Segmentación de las órdenes confirmadas según el margen teórico cotizado. Haz clic en cualquier rango para auditarlo en detalle.</span>
                </v-tooltip>
              </div>
              <div class="panel-sub">Segmentación de órdenes confirmadas según umbral de rentabilidad teórica</div>
            </div>
            <div class="caption grey--text text--darken-2 font-weight-medium">
              Venta evaluada: <span class="font-weight-black grey--text text--darken-4">{{ totalMarginSales | moneda }}</span>
            </div>
          </div>

          <!-- Stacked Segmented Bar -->
          <div class="margin-stacked-track mb-4">
            <div
              v-if="marginHealthyPct > 0"
              class="stacked-segment is-healthy"
              :style="{ width: marginHealthyPct + '%' }"
              @click="openMarginModal('healthy')"
            >
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="segment-label">{{ marginHealthyPct.toFixed(1) }}%</span>
                </template>
                <span>Sano (≥30%): {{ (marginBands.healthy && marginBands.healthy.sales) | moneda }} ({{ (marginBands.healthy && marginBands.healthy.count) || 0 }} OT)</span>
              </v-tooltip>
            </div>
            <div
              v-if="marginOkPct > 0"
              class="stacked-segment is-ok"
              :style="{ width: marginOkPct + '%' }"
              @click="openMarginModal('ok')"
            >
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="segment-label">{{ marginOkPct.toFixed(1) }}%</span>
                </template>
                <span>Aceptable (15–30%): {{ (marginBands.ok && marginBands.ok.sales) | moneda }} ({{ (marginBands.ok && marginBands.ok.count) || 0 }} OT)</span>
              </v-tooltip>
            </div>
            <div
              v-if="marginTightPct > 0"
              class="stacked-segment is-tight"
              :style="{ width: marginTightPct + '%' }"
              @click="openMarginModal('tight')"
            >
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="segment-label">{{ marginTightPct.toFixed(1) }}%</span>
                </template>
                <span>Ajustado (0–15%): {{ (marginBands.tight && marginBands.tight.sales) | moneda }} ({{ (marginBands.tight && marginBands.tight.count) || 0 }} OT)</span>
              </v-tooltip>
            </div>
            <div
              v-if="marginLossPct > 0"
              class="stacked-segment is-loss"
              :style="{ width: marginLossPct + '%' }"
              @click="openMarginModal('loss')"
            >
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="segment-label">{{ marginLossPct.toFixed(1) }}%</span>
                </template>
                <span>Pérdida (&lt;0%): {{ (marginBands.loss && marginBands.loss.sales) | moneda }} ({{ (marginBands.loss && marginBands.loss.count) || 0 }} OT)</span>
              </v-tooltip>
            </div>
          </div>

          <!-- 4 Interactive Tier Buttons -->
          <v-row dense>
            <v-col cols="12" sm="6" md="3">
              <div class="margin-tier-btn is-healthy" @click="openMarginModal('healthy')">
                <div class="d-flex align-center justify-space-between">
                  <span class="tier-name">Sano ≥ 30%</span>
                  <span class="tier-count">{{ (marginBands.healthy && marginBands.healthy.count) || 0 }} OT</span>
                </div>
                <div class="tier-sales">{{ (marginBands.healthy && marginBands.healthy.sales) | moneda }}</div>
                <div class="tier-share">{{ marginHealthyPct.toFixed(1) }}% de la venta · Auditar →</div>
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="margin-tier-btn is-ok" @click="openMarginModal('ok')">
                <div class="d-flex align-center justify-space-between">
                  <span class="tier-name">Aceptable 15–30%</span>
                  <span class="tier-count">{{ (marginBands.ok && marginBands.ok.count) || 0 }} OT</span>
                </div>
                <div class="tier-sales">{{ (marginBands.ok && marginBands.ok.sales) | moneda }}</div>
                <div class="tier-share">{{ marginOkPct.toFixed(1) }}% de la venta · Auditar →</div>
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="margin-tier-btn is-tight" @click="openMarginModal('tight')">
                <div class="d-flex align-center justify-space-between">
                  <span class="tier-name">Ajustado 0–15%</span>
                  <span class="tier-count">{{ (marginBands.tight && marginBands.tight.count) || 0 }} OT</span>
                </div>
                <div class="tier-sales">{{ (marginBands.tight && marginBands.tight.sales) | moneda }}</div>
                <div class="tier-share">{{ marginTightPct.toFixed(1) }}% de la venta · Auditar →</div>
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="margin-tier-btn is-loss" @click="openMarginModal('loss')">
                <div class="d-flex align-center justify-space-between">
                  <span class="tier-name">Pérdida &lt; 0%</span>
                  <span class="tier-count">{{ (marginBands.loss && marginBands.loss.count) || 0 }} OT</span>
                </div>
                <div class="tier-sales">{{ (marginBands.loss && marginBands.loss.sales) | moneda }}</div>
                <div class="tier-share">{{ marginLossPct.toFixed(1) }}% de la venta · Auditar →</div>
              </div>
            </v-col>
          </v-row>
        </v-card>

        <!-- Row 3: Evolución Temporal (Line Chart) -->
        <v-card class="panel-card mb-6 shadow-xs" flat>
          <div class="legend-row">
            <div>
              <div class="panel-title">Evolución en el Tiempo</div>
              <div class="panel-sub">Comparativa diaria de cotizado vs. venta confirmada</div>
            </div>
            <div class="legend">
              <span><i class="swatch quoted"></i> Cotizado</span>
              <span><i class="swatch sold"></i> Vendido</span>
            </div>
          </div>
          <div v-if="!(daily && daily.length)" class="empty-mini py-12 text-center">
            <v-icon color="grey lighten-1" class="mb-2" large>fas fa-chart-area</v-icon>
            <div>Sin movimientos comerciales registrados en este período</div>
          </div>
          <div v-else style="height: 300px">
            <line-chart ref="salesLineChart" :key="chartKey" :chart-data="lineData" :options="lineOptions" :height="300" />
          </div>
        </v-card>

        <!-- Row 4: Rankings por Ejecutivo y Cliente (Interactivos) -->
        <v-row dense class="mb-6">
          <v-col cols="12" md="6">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Por Ejecutivo Comercial</div>
                <span class="caption grey--text font-weight-bold">{{ sellers.length }} activos</span>
              </div>
              <div class="panel-sub mb-4">Venta confirmada · Haz clic para ver sus órdenes</div>
              <div v-if="!sellers.length" class="empty-mini py-6 text-center">Sin ventas confirmadas</div>
              <div
                v-for="row in sellers"
                :key="'s-' + row.id"
                class="rank-row is-clickable"
                @click="openSellerModal(row)"
              >
                <div class="d-flex align-center justify-space-between">
                  <div class="rank-name font-weight-bold text-truncate" style="max-width: 65%">
                    {{ row.name }}
                  </div>
                  <div class="rank-val font-weight-bold">{{ row.total | moneda }}</div>
                </div>
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="rank-meta">{{ row.count }} OT confirmadas</div>
                  <span class="caption primary--text font-weight-bold action-hint">Auditar órdenes →</span>
                </div>
                <div class="rank-track">
                  <div class="rank-fill" :style="{ width: rankWidth(row.total, maxSeller) }"></div>
                </div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Por Cliente</div>
                <span class="caption grey--text font-weight-bold">{{ customers.length }} activos</span>
              </div>
              <div class="panel-sub mb-4">Venta confirmada · Haz clic para ver su ficha</div>
              <div v-if="!customers.length" class="empty-mini py-6 text-center">Sin clientes con venta</div>
              <div
                v-for="row in customers"
                :key="'c-' + row.id"
                class="rank-row is-clickable"
                @click="openCustomerModal(row)"
              >
                <div class="d-flex align-center justify-space-between">
                  <div class="rank-name font-weight-bold text-truncate" style="max-width: 65%">
                    {{ row.name }}
                  </div>
                  <div class="rank-val font-weight-bold">{{ row.total | moneda }}</div>
                </div>
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="rank-meta">{{ row.count }} OT confirmadas</div>
                  <span class="caption teal--text text--darken-2 font-weight-bold action-hint" @click.stop="openCustomerHistory(row.id)">Ver ficha 360° →</span>
                </div>
                <div class="rank-track">
                  <div class="rank-fill is-teal" :style="{ width: rankWidth(row.total, maxCustomer) }"></div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Row 5: Rankings de Productos (Top vs Peor Margen Clarificado) -->
        <v-row dense>
          <v-col cols="12" md="6">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Productos Más Vendidos</div>
                <span class="caption grey--text font-weight-bold">Por volumen ($)</span>
              </div>
              <div class="panel-sub mb-4">Ranking por venta confirmada y margen teórico</div>
              <div v-if="!products.length" class="empty-mini py-6 text-center">Sin productos vendidos</div>
              <div v-for="row in products" :key="'p-' + (row.sku || row.name)" class="rank-row">
                <div class="d-flex align-center justify-space-between">
                  <div class="rank-name font-weight-bold text-truncate" style="max-width: 65%">
                    {{ row.name }}
                  </div>
                  <div class="rank-val font-weight-bold">{{ row.sales | moneda }}</div>
                </div>
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="rank-meta">{{ row.sku || 'Sin SKU' }} · {{ row.qty }} unidades</div>
                  <span class="margin-pill" :class="marginClass(row.margin)">
                    {{ row.margin | percent(1) }} margen
                  </span>
                </div>
                <div class="rank-track">
                  <div class="rank-fill is-navy" :style="{ width: rankWidth(row.sales, maxProduct) }"></div>
                </div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title error--text text--darken-2">
                  <v-icon x-small color="error" class="mr-1">fas fa-exclamation-triangle</v-icon>
                  Peor Margen Teórico
                </div>
                <span class="caption error--text font-weight-bold">Alerta de Rentabilidad</span>
              </div>
              <div class="panel-sub mb-4">Productos vendidos con menor margen cotizado (fuga de utilidad)</div>
              <div v-if="!worstProducts.length" class="empty-mini py-6 text-center">Sin productos para rankear</div>
              <div v-for="row in worstProducts" :key="'w-' + (row.sku || row.name)" class="rank-row is-loss-row">
                <div class="d-flex align-center justify-space-between">
                  <div class="rank-name font-weight-bold text-truncate" style="max-width: 60%">
                    {{ row.name }}
                  </div>
                  <div class="rank-val font-weight-bold grey--text text--darken-3">{{ row.sales | moneda }}</div>
                </div>
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="rank-meta">{{ row.sku || 'Sin SKU' }} · {{ row.qty }} unidades</div>
                  <!-- Clear severity badge: shows negative percentage clearly without fake sales volume bars -->
                  <span class="margin-pill is-loss font-weight-black">
                    {{ row.margin | percent(1) }}
                  </span>
                </div>
                <div class="rank-track">
                  <div class="rank-fill is-rose" :style="{ width: worstSeverityWidth(row.margin) }"></div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </div>

    <!-- Modal: Auditoría & Drill-Down -->
    <v-dialog
      v-model="auditModal"
      max-width="480"
    >
      <v-card v-if="modalItem" class="rounded-xl pa-6">
        <div class="d-flex align-center justify-space-between mb-4">
          <div class="d-flex align-center">
            <v-avatar size="42" :color="modalItem.avatarColor || 'primary lighten-5'" class="mr-3 rounded-lg">
              <v-icon small :color="modalItem.iconColor || 'primary'">{{ modalItem.icon || 'fas fa-search' }}</v-icon>
            </v-avatar>
            <div>
              <div class="caption font-weight-black text-uppercase grey--text">{{ modalItem.category }}</div>
              <h3 class="text-subtitle-1 font-weight-black grey--text text--darken-4 mb-0 text-truncate" style="max-width: 290px">
                {{ modalItem.title }}
              </h3>
            </div>
          </div>
          <v-btn icon small @click="auditModal = false">
            <v-icon small>fas fa-times</v-icon>
          </v-btn>
        </div>

        <v-divider class="mb-4"></v-divider>

        <v-card outlined flat class="pa-4 rounded-xl mb-4 bg-light-surface">
          <div class="caption grey--text font-weight-bold text-uppercase mb-1">Monto en el período</div>
          <div class="text-h5 font-weight-black grey--text text--darken-4">{{ modalItem.total | moneda }}</div>
          <div class="caption font-weight-bold grey--text text--darken-2 mt-1">
            {{ modalItem.count }} órdenes de trabajo confirmadas
          </div>
        </v-card>

        <div v-if="modalItem.description" class="body-2 grey--text text--darken-2 mb-6">
          {{ modalItem.description }}
        </div>

        <div class="d-flex flex-column" style="gap: 10px">
          <v-btn
            v-if="modalItem.actionType === 'seller'"
            block
            color="primary"
            depressed
            class="rounded-xl py-5 font-weight-bold text-none"
            @click="goToSellerNotes(modalItem.id)"
          >
            <v-icon left small>fas fa-file-invoice-dollar</v-icon>
            Ver Notas de Venta del Ejecutivo
          </v-btn>

          <v-btn
            v-if="modalItem.actionType === 'customer'"
            block
            color="teal"
            dark
            depressed
            class="rounded-xl py-5 font-weight-bold text-none"
            @click="openCustomerHistory(modalItem.id)"
          >
            <v-icon left small>fas fa-chart-pie</v-icon>
            Ver Ficha 360° e Histórico del Cliente
          </v-btn>

          <v-btn
            v-if="modalItem.actionType === 'margin'"
            block
            color="error"
            depressed
            class="rounded-xl py-5 font-weight-bold text-none"
            @click="goToSalesNotesFiltered()"
          >
            <v-icon left small>fas fa-search</v-icon>
            Auditar Órdenes en Notas de Venta
          </v-btn>

          <v-btn
            block
            outlined
            color="grey darken-1"
            class="rounded-xl py-4 font-weight-bold text-none"
            @click="auditModal = false"
          >
            Cerrar
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Modal Full-Page: Ficha 360° Histórico de Cliente -->
    <customer-history-modal
      v-model="customerHistoryModal"
      :customer-id="selectedCustomerId"
      :initial-start-date="startDate"
      :initial-end-date="endDate"
    />
  </v-container>
</template>

<script>
import { fetchCommercialDashboard } from '@/api/reports'
import { generateCommercialDashboardPdf } from '@/doc_generators/commercialDashboardReport'
import LineChart from '@/components/charts/LineChart.vue'
import CustomerHistoryModal from '@/components/customers/CustomerHistoryModal.vue'
import notifications from '@/utils/notifications'
import moment from 'moment'
import numeral from 'numeral'
import businessLineFilter from '@/mixins/businessLineFilter'

export default {
  name: 'CommercialDashboard',
  mixins: [businessLineFilter],
  components: { LineChart, CustomerHistoryModal },
  data() {
    return {
      loading: true,
      ready: false,
      requesting: false,
      downloadingPdf: false,
      error: '',
      startDate: moment().startOf('month').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
      activePreset: 'month',
      lastQuery: '',
      asOf: '',
      updatedAt: '',
      serverTimezone: '',
      auditModal: false,
      modalItem: null,
      customerHistoryModal: false,
      selectedCustomerId: null,
      presets: [
        { id: 'today', label: 'Hoy' },
        { id: '7d', label: '7 días' },
        { id: 'month', label: 'Mes actual' },
        { id: 'prev_month', label: 'Mes anterior' },
        { id: 'qtd', label: 'Trimestre (QTD)' },
        { id: 'ytd', label: 'Año (YTD)' }
      ],
      lineData: { labels: [], datasets: [] },
      kpis: {
        quoted: 0,
        count: 0,
        confirmed: 0,
        confirmed_count: 0,
        conversion: 0,
        today_confirmed: 0,
        yesterday_confirmed: 0,
        vs_yesterday_pct: null,
        vs_previous_pct: null,
        confirmed_margin: 0,
        quoted_margin: 0,
        confirmed_cost: 0,
        confirmed_profit: 0,
        vs_previous_margin_pp: 0,
        pipeline: 0,
        pipeline_count: 0
      },
      sellers: [],
      customers: [],
      products: [],
      worstProducts: [],
      marginBands: {
        healthy: { count: 0, sales: 0 },
        ok: { count: 0, sales: 0 },
        tight: { count: 0, sales: 0 },
        loss: { count: 0, sales: 0 }
      },
      daily: []
    }
  },
  computed: {
    periodLabel() {
      const start = this.startDate ? moment(this.startDate).format('DD/MM/YYYY') : '—'
      const end = this.endDate ? moment(this.endDate).format('DD/MM/YYYY') : '—'
      return `${start} al ${end}`
    },
    companyTimeZone() {
      const company = this.$store.getters.getCompany || {}
      return company.current_time_zone || this.serverTimezone || 'America/Santiago'
    },
    formattedAsOf() {
      const ts = this.updatedAt || this.asOf
      if (!ts) return ''
      const m = moment(ts)
      const dateText = m.isValid() ? m.format('DD/MM/YYYY HH:mm') : this.asOf
      return `${dateText} (${this.companyTimeZone})`
    },
    totalMarginSales() {
      const h = Number((this.marginBands.healthy && this.marginBands.healthy.sales) || 0)
      const o = Number((this.marginBands.ok && this.marginBands.ok.sales) || 0)
      const t = Number((this.marginBands.tight && this.marginBands.tight.sales) || 0)
      const l = Number((this.marginBands.loss && this.marginBands.loss.sales) || 0)
      return h + o + t + l
    },
    marginHealthyPct() {
      if (!this.totalMarginSales) return 0
      return ((Number(this.marginBands.healthy && this.marginBands.healthy.sales) || 0) / this.totalMarginSales) * 100
    },
    marginOkPct() {
      if (!this.totalMarginSales) return 0
      return ((Number(this.marginBands.ok && this.marginBands.ok.sales) || 0) / this.totalMarginSales) * 100
    },
    marginTightPct() {
      if (!this.totalMarginSales) return 0
      return ((Number(this.marginBands.tight && this.marginBands.tight.sales) || 0) / this.totalMarginSales) * 100
    },
    marginLossPct() {
      if (!this.totalMarginSales) return 0
      return ((Number(this.marginBands.loss && this.marginBands.loss.sales) || 0) / this.totalMarginSales) * 100
    },
    maxSeller() {
      return this.maxOf(this.sellers)
    },
    maxCustomer() {
      return this.maxOf(this.customers)
    },
    maxProduct() {
      return this.maxOf(this.products, 'sales')
    },
    maxWorst() {
      return this.maxOf(this.worstProducts, 'sales')
    },
    chartKey() {
      return `${this.startDate || ''}-${this.endDate || ''}-${this.daily.length}`
    },
    lineOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          mode: 'index',
          intersect: false,
          backgroundColor: '#0f172a',
          titleFontColor: '#f8fafc',
          bodyFontColor: '#cbd5e1',
          borderColor: '#1e293b',
          borderWidth: 1,
          cornerRadius: 8,
          xPadding: 12,
          yPadding: 12,
          callbacks: {
            label: (item, data) => `${data.datasets[item.datasetIndex].label}: $${numeral(item.yLabel).format('0,0')}`
          }
        },
        scales: {
          xAxes: [{
            gridLines: { display: false },
            ticks: { fontColor: '#64748b', fontSize: 11, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 }
          }],
          yAxes: [{
            gridLines: { color: '#f1f5f9', borderDash: [4, 4], drawBorder: false },
            ticks: {
              fontColor: '#64748b',
              fontSize: 11,
              callback: (value) => '$' + numeral(value).format('0,0a')
            }
          }]
        }
      }
    }
  },
  methods: {
    maxOf(rows, key) {
      let max = 1
      const field = key || 'total'
      ;(rows || []).forEach((row) => {
        const value = Number(row[field] || 0)
        if (value > max) max = value
      })
      return max
    },
    rankWidth(value, max) {
      return Math.max(8, Math.round((Number(value || 0) / Math.max(1, max)) * 100)) + '%'
    },
    worstSeverityWidth(margin) {
      const num = Number(margin || 0)
      if (num >= 0) return '12%'
      return Math.min(100, Math.max(15, Math.round(Math.abs(num) * 100))) + '%'
    },
    buildChart() {
      const days = this.daily || []
      this.lineData = {
        labels: days.map((row) => row.label),
        datasets: [
          {
            label: 'Cotizado',
            data: days.map((row) => Number(row.quoted || 0)),
            borderColor: '#94a3b8',
            backgroundColor: 'transparent',
            borderWidth: 2,
            fill: false,
            lineTension: 0.35,
            pointRadius: 2.5,
            pointBackgroundColor: '#94a3b8'
          },
          {
            label: 'Vendido',
            data: days.map((row) => Number(row.confirmed || 0)),
            borderColor: '#0284c7',
            backgroundColor: 'rgba(2, 132, 199, 0.12)',
            borderWidth: 2.5,
            fill: true,
            lineTension: 0.35,
            pointRadius: 2.5,
            pointBackgroundColor: '#0284c7'
          }
        ]
      }
    },
    deltaClass(value) {
      if (value == null) return ''
      if (value > 0) return 'is-up'
      if (value < 0) return 'is-down'
      return ''
    },
    deltaLabel(value) {
      if (value == null) return '—'
      const sign = value > 0 ? '+' : ''
      return `${sign}${value}%`
    },
    deltaPpLabel(value) {
      if (value == null) return '—'
      const sign = value > 0 ? '+' : ''
      return `${sign}${value} pp`
    },
    marginClass(value) {
      if (value < 0) return 'is-loss'
      if (value < 0.15) return 'is-warn'
      return 'is-ok'
    },
    getCompanyDate() {
      const tz = this.companyTimeZone || 'America/Santiago'
      try {
        const parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: tz,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }).formatToParts(new Date()).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {})
        return moment(`${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`)
      } catch (e) {
        return moment()
      }
    },
    applyPreset(preset) {
      this.activePreset = preset.id
      const base = this.getCompanyDate()
      const today = base.format('YYYY-MM-DD')
      if (preset.id === 'today') {
        this.startDate = today
        this.endDate = today
      } else if (preset.id === '7d') {
        this.startDate = base.clone().subtract(6, 'days').format('YYYY-MM-DD')
        this.endDate = today
      } else if (preset.id === 'month') {
        this.startDate = base.clone().startOf('month').format('YYYY-MM-DD')
        this.endDate = today
      } else if (preset.id === 'prev_month') {
        this.startDate = base.clone().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
        this.endDate = base.clone().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
      } else if (preset.id === 'qtd') {
        this.startDate = base.clone().startOf('quarter').format('YYYY-MM-DD')
        this.endDate = today
      } else if (preset.id === 'ytd') {
        this.startDate = base.clone().startOf('year').format('YYYY-MM-DD')
        this.endDate = today
      }
      this.syncUrlAndLoad()
    },
    onManualDateChange() {
      this.activePreset = 'custom'
      if (this.startDate && this.endDate && this.startDate > this.endDate) {
        const temp = this.startDate
        this.startDate = this.endDate
        this.endDate = temp
      }
      this.syncUrlAndLoad()
    },
    syncUrlAndLoad() {
      const query = {
        start: this.startDate,
        end: this.endDate
      }
      if (this.line) query.line = this.line
      if (
        this.$route.query.start !== query.start ||
        this.$route.query.end !== query.end ||
        (this.$route.query.line || '') !== (query.line || '')
      ) {
        this.$router.replace({ query }).catch(() => {})
      }
      this.load(true)
    },
    async load(force) {
      const defaultStart = this.getCompanyDate().startOf('month').format('YYYY-MM-DD')
      const defaultEnd = this.getCompanyDate().format('YYYY-MM-DD')
      const start = this.startDate || defaultStart
      const end = this.endDate || defaultEnd
      const tz = this.companyTimeZone
      const query = `start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}&timezone=${encodeURIComponent(tz)}${this.lineQuery()}`
      if (this.requesting) return
      if (!force && this.lastQuery === query && this.asOf) return
      this.requesting = true
      this.loading = true
      this.error = ''
      try {
        const data = await fetchCommercialDashboard(query)
        if (!data || !data.as_of) throw new Error('Respuesta vacía del servidor')
        this.asOf = data.as_of
        this.updatedAt = data.updated_at || ''
        this.serverTimezone = data.timezone || ''
        this.lastQuery = query
        this.kpis = Object.assign({}, this.kpis, data.kpis || {})
        this.setLineBreakdown(data.lines)
        this.sellers = data.sellers || []
        this.customers = data.customers || []
        this.products = data.products || []
        this.worstProducts = data.worst_products || []
        this.marginBands = Object.assign({
          healthy: { count: 0, sales: 0 },
          ok: { count: 0, sales: 0 },
          tight: { count: 0, sales: 0 },
          loss: { count: 0, sales: 0 }
        }, data.margin_bands || {})
        this.daily = data.daily || []
        this.buildChart()
        this.ready = true
      } catch (err) {
        this.error = 'No se pudo cargar el dashboard de ventas. Revisa tu conexión o el rango seleccionado.'
        notifications.showError('Error cargando dashboard de ventas: ' + err)
      } finally {
        this.requesting = false
        this.loading = false
      }
    },
    openSellerModal(row) {
      this.modalItem = {
        actionType: 'seller',
        category: 'Ejecutivo Comercial',
        title: row.name,
        id: row.id,
        total: row.total,
        count: row.count,
        icon: 'fas fa-user-tie',
        avatarColor: 'primary lighten-5',
        iconColor: 'primary',
        description: `Rendimiento acumulado de ${row.name} en el período seleccionado. Puedes inspeccionar las notas de venta individuales emitidas y aprobadas.`
      }
      this.auditModal = true
    },
    openCustomerModal(row) {
      this.modalItem = {
        actionType: 'customer',
        category: 'Cliente',
        title: row.name,
        id: row.id,
        total: row.total,
        count: row.count,
        icon: 'fas fa-building',
        avatarColor: 'teal lighten-5',
        iconColor: 'teal',
        description: `Compras confirmadas por ${row.name} en este rango. Accede a su dashboard comercial histórico o filtra sus documentos.`
      }
      this.auditModal = true
    },
    openMarginModal(bandKey) {
      const map = {
        healthy: { title: 'Margen Sano (≥ 30%)', color: 'success', icon: 'fas fa-check-circle' },
        ok: { title: 'Margen Aceptable (15–30%)', color: 'primary', icon: 'fas fa-thumbs-up' },
        tight: { title: 'Margen Ajustado (0–15%)', color: 'warning', icon: 'fas fa-exclamation-circle' },
        loss: { title: 'Margen en Pérdida (< 0%)', color: 'error', icon: 'fas fa-times-circle' }
      }
      const cfg = map[bandKey] || { title: 'Banda de Margen', color: 'primary', icon: 'fas fa-chart-pie' }
      const band = this.marginBands[bandKey] || { count: 0, sales: 0 }
      this.modalItem = {
        actionType: 'margin',
        category: 'Auditoría de Rentabilidad',
        title: cfg.title,
        id: bandKey,
        total: band.sales,
        count: band.count,
        icon: cfg.icon,
        avatarColor: `${cfg.color} lighten-5`,
        iconColor: cfg.color,
        description: `Este segmento agrupa las órdenes confirmadas con un rendimiento teórico ${cfg.title.toLowerCase()}. Revisa las cotizaciones que originaron estos costos para corregir desviaciones.`
      }
      this.auditModal = true
    },
    goToSellerNotes(sellerId) {
      this.auditModal = false
      this.$router.push({
        path: '/sales',
        query: {
          customer__seller: sellerId,
          created_after: this.startDate,
          created_before: this.endDate,
          has_work_order: 'true'
        }
      }).catch(() => {})
    },
    openCustomerHistory(customerId) {
      this.auditModal = false
      this.selectedCustomerId = customerId
      this.customerHistoryModal = true
    },
    goToCustomerDashboard(customerId) {
      this.openCustomerHistory(customerId)
    },
    goToSalesNotesFiltered() {
      this.auditModal = false
      this.$router.push({
        path: '/sales',
        query: {
          created_after: this.startDate,
          created_before: this.endDate,
          has_work_order: 'true'
        }
      }).catch(() => {})
    },
    async downloadExecutivePdf() {
      if (this.downloadingPdf) return
      this.downloadingPdf = true
      try {
        let chartImageBase64 = null
        try {
          const chartEl = this.$refs.salesLineChart?.$el
          const canvas = chartEl?.tagName === 'CANVAS' ? chartEl : chartEl?.querySelector?.('canvas')
          if (canvas && typeof canvas.toDataURL === 'function') {
            chartImageBase64 = canvas.toDataURL('image/png', 2)
          }
        } catch (e) {
          console.warn('Could not extract chart canvas image:', e)
        }

        const company = this.$store.getters.getCompany || {}
        await generateCommercialDashboardPdf({
          company,
          startDate: this.startDate,
          endDate: this.endDate,
          serverTimezone: this.serverTimezone,
          lineName: this.selectedLineName,
          lines: this.lineBreakdown,
          kpis: this.kpis,
          marginBands: this.marginBands,
          sellers: this.sellers,
          customers: this.customers,
          worstMarginOrders: this.worstProducts,
          chartImageBase64
        })
        notifications.showSuccess('Reporte PDF ejecutivo generado correctamente.')
      } catch (err) {
        notifications.showError('No se pudo generar el reporte PDF: ' + (err.message || err))
      } finally {
        this.downloadingPdf = false
      }
    }
  },
  watch: {
    line(val, oldVal) {
      if (!this.ready || String(val) === String(oldVal)) return
      this.syncUrlAndLoad()
    }
  },
  mounted() {
    if (this.$route.query.line) this.line = String(this.$route.query.line)
    if (this.$route.query.start && this.$route.query.end) {
      this.startDate = this.$route.query.start
      this.endDate = this.$route.query.end
      this.activePreset = 'custom'
    } else {
      this.startDate = this.getCompanyDate().startOf('month').format('YYYY-MM-DD')
      this.endDate = this.getCompanyDate().format('YYYY-MM-DD')
    }
    this.load(true)
  }
}
</script>

<style scoped>
.dashboard-container {
  background-color: #f8fafc;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.border-bottom {
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.shadow-xs {
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04), 0 1px 2px rgba(15, 23, 42, 0.02);
}

.tracking-tight {
  letter-spacing: -0.02em;
}

/* Daily Pulse Ticker */
.daily-pulse-card {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
}

.pulse-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
  animation: pulse 2s infinite ease-in-out;
}

@keyframes pulse {
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); }
  70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
}

/* Filters */
.filter-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.compact-date-input {
  max-width: 155px;
}

/* Hero KPIs */
.hero-kpi-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.hero-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}

.hero-kpi-card.is-primary {
  border-color: #bae6fd;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.kpi-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.kpi-value {
  font-size: 1.55rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.2;
}

.kpi-footer {
  border-top: 1px solid #f1f5f9;
}

/* Margin Distribution Track */
.margin-stacked-track {
  display: flex;
  height: 28px;
  border-radius: 8px;
  overflow: hidden;
  background: #f1f5f9;
}

.stacked-segment {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.stacked-segment:hover {
  filter: brightness(0.92);
}

.stacked-segment.is-healthy { background: #10b981; }
.stacked-segment.is-ok { background: #0284c7; }
.stacked-segment.is-tight { background: #f59e0b; }
.stacked-segment.is-loss { background: #e11d48; }

.segment-label {
  font-size: 11px;
  font-weight: 800;
  color: white;
  padding: 0 4px;
}

/* Margin Tier Quick Buttons */
.margin-tier-btn {
  padding: 12px 14px;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.15s ease, border-color 0.15s ease;
}

.margin-tier-btn:hover {
  transform: translateY(-2px);
}

.margin-tier-btn.is-healthy {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
}
.margin-tier-btn.is-healthy .tier-name { color: #047857; }

.margin-tier-btn.is-ok {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
}
.margin-tier-btn.is-ok .tier-name { color: #0369a1; }

.margin-tier-btn.is-tight {
  background: #fffbeb;
  border: 1px solid #fde68a;
}
.margin-tier-btn.is-tight .tier-name { color: #b45309; }

.margin-tier-btn.is-loss {
  background: #fff1f2;
  border: 1px solid #fecdd3;
}
.margin-tier-btn.is-loss .tier-name { color: #be123c; }

.tier-name {
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.tier-count {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
}

.tier-sales {
  font-size: 1.15rem;
  font-weight: 800;
  color: #0f172a;
  margin-top: 4px;
  letter-spacing: -0.02em;
}

.tier-share {
  font-size: 0.7rem;
  font-weight: 600;
  color: #64748b;
  margin-top: 2px;
}

/* Panels */
.panel-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 22px;
}

.panel-title {
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.panel-sub {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  margin-top: 2px;
}

.legend-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 16px;
}

.legend {
  display: flex;
  gap: 16px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.swatch {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}

.swatch.quoted { background: #94a3b8; }
.swatch.sold { background: #0284c7; }

/* Rankings & Tracks */
.rank-row {
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  transition: background 0.15s ease;
}

.rank-row.is-clickable {
  cursor: pointer;
}

.rank-row.is-clickable:hover {
  background: #f8fafc;
}

.rank-row.is-clickable:hover .action-hint {
  text-decoration: underline;
}

.action-hint {
  opacity: 0.85;
}

.rank-name {
  font-size: 13px;
  color: #0f172a;
}

.rank-meta {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}

.rank-val {
  font-size: 13px;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.rank-track {
  height: 5px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
  margin-top: 4px;
}

.rank-fill {
  height: 100%;
  background: #0284c7;
  border-radius: 999px;
}

.rank-fill.is-teal { background: #0d9488; }
.rank-fill.is-navy { background: #0f172a; }
.rank-fill.is-rose { background: #e11d48; }

.margin-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
}

.margin-pill.is-ok, .is-ok { color: #047857; background: #ecfdf5; }
.margin-pill.is-warn, .is-warn { color: #b45309; background: #fffbeb; }
.margin-pill.is-loss, .is-loss { color: #be123c; background: #fff1f2; }

.delta {
  font-size: 0.72rem;
  font-weight: 800;
  color: #94a3b8;
}

.delta.is-up { color: #059669; }
.delta.is-down { color: #e11d48; }

.empty-mini {
  font-size: 13px;
  font-weight: 600;
  color: #94a3b8;
}

.bg-light-surface {
  background-color: #f8fafc;
}

.line-table >>> th {
  font-size: 11px !important;
  font-weight: 800 !important;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b !important;
}

.line-row {
  cursor: pointer;
}

.is-selected-line {
  background: #f0fdf4;
  box-shadow: inset 3px 0 0 #00a650;
}
</style>
